import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, List, GridFour, Plus, CaretLeft, CaretRight,
  PencilSimple, Trash, PaperPlaneTilt, Paperclip, X, DownloadSimple, Clock, MapPin, Books,
} from "@phosphor-icons/react";

const DAYS_FR = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const DAYS_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const HOURS = Array.from({ length: 13 }, (_, i) => 7 + i); // 07:00 → 19:00

const ACCEPT_MIME = "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png,application/zip,application/x-zip-compressed";

const KIND_LABEL = {
  homework: "Devoir", quiz: "Interro.", project: "Projet",
  exam: "Examen", essay: "Rédaction", presentation: "Exposé",
};

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysBetween(a, b) {
  const out = [];
  const cur = new Date(a);
  while (cur <= b) { out.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return out;
}

export default function Agenda() {
  const { user } = useAuth();
  const canEditSlot = user && ["super_admin", "owner", "admin", "director", "study_director", "secretary", "teacher"].includes(user.role);
  const canEditAssign = canEditSlot;
  const isStudent = user?.role === "student";

  const [view, setView] = useState("month"); // month | week | list
  const [cursor, setCursor] = useState(new Date());

  const [assignments, setAssignments] = useState([]);
  const [slots, setSlots] = useState([]);
  const [personalEvents, setPersonalEvents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const [openEvent, setOpenEvent] = useState(null); // {type, data}
  const [openSlot, setOpenSlot] = useState(null);   // slot object (or {} for new)
  const [openAssign, setOpenAssign] = useState(null); // assign obj (or {} for new)
  const [openSubmit, setOpenSubmit] = useState(null); // assignment obj (student)
  const [openPersonal, setOpenPersonal] = useState(null); // personal event obj (or {} for new)

  const loadAll = async () => {
    try {
      const reqs = [
        api.get("/timetable-slots").catch(() => ({ data: [] })),
        api.get("/courses").catch(() => ({ data: [] })),
        api.get("/subjects").catch(() => ({ data: [] })),
        api.get("/classes").catch(() => ({ data: [] })),
        api.get("/teachers").catch(() => ({ data: [] })),
        api.get("/personal-events").catch(() => ({ data: [] })),
      ];
      if (isStudent) reqs.push(api.get("/parent/assignments").catch(() => ({ data: { items: [] } })));
      else reqs.push(api.get("/assignments").catch(() => ({ data: [] })));
      const [s, c, sb, cl, t, pe, a] = await Promise.all(reqs);
      setSlots(s.data || []);
      setCourses(c.data || []);
      setSubjects(sb.data || []);
      setClasses(cl.data || []);
      setTeachers(t.data || []);
      setPersonalEvents(pe.data || []);
      setAssignments(isStudent ? (a.data?.items || []) : (a.data || []));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  // ---- Browser push notifications for personal events (5 min before start_time) ----
  useEffect(() => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      Notification.requestPermission().then(p => setNotifPermission(p));
    }
  }, []);
  useEffect(() => {
    if (typeof Notification === "undefined") return;
    const KEY = "kosyam_notified_events";
    const load = () => new Set(JSON.parse(localStorage.getItem(KEY) || "[]"));
    const save = (s) => localStorage.setItem(KEY, JSON.stringify([...s]));
    const check = () => {
      if (Notification.permission !== "granted") return;
      const notified = load();
      const now = new Date();
      const in5min = new Date(now.getTime() + 5 * 60000);
      personalEvents.forEach(e => {
        if (!e.start_time || notified.has(e.id)) return;
        const [h, m] = e.start_time.split(":").map(Number);
        const dt = new Date(e.date);
        dt.setHours(h || 0, m || 0, 0, 0);
        if (dt > now && dt <= in5min) {
          try {
            new Notification(`⏰ ${e.title}`, {
              body: `Dans ${Math.max(1, Math.round((dt - now) / 60000))} min${e.location ? ` · ${e.location}` : ""}`,
              icon: "/favicon.ico", tag: `kosyam-${e.id}`,
            });
            notified.add(e.id);
          } catch (_) { /* ignore */ }
        }
      });
      save(notified);
    };
    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, [personalEvents]);

  const subjectById = useMemo(() => Object.fromEntries(subjects.map(s => [s.id, s])), [subjects]);
  const classById = useMemo(() => Object.fromEntries(classes.map(c => [c.id, c])), [classes]);
  const teacherById = useMemo(() => Object.fromEntries(teachers.map(t => [t.id, t])), [teachers]);
  const courseById = useMemo(() => Object.fromEntries(courses.map(c => [c.id, c])), [courses]);

  // Enrich a course_id -> {subject, class, teachers}
  const enrichCourse = (course_id) => {
    const c = courseById[course_id] || {};
    return {
      subject_name: subjectById[c.subject_id]?.name || "—",
      class_name: classById[c.class_group_id]?.name || "—",
      class_id: c.class_group_id,
      teacher_names: (c.teacher_ids || []).map(tid => teacherById[tid]?.name || "?"),
      room: c.room || "",
    };
  };

  // Build event list (dated), split into assignments (single date) + slots (recurring)
  const assignmentEvents = useMemo(() => {
    return assignments.filter(a => a.due_date).map(a => {
      const enriched = enrichCourse(a.course_id);
      return {
        ...enriched, ...a,
        _type: "assignment",
        _date: new Date(a.due_date),
        subject_name: a.subject_name || enriched.subject_name,
      };
    });
  }, [assignments, courses, subjects, classes, teachers]);

  // Expand slots for a week starting on `weekStart`
  const expandSlotsForWeek = (weekStart) => {
    const out = [];
    slots.forEach(s => {
      const enriched = enrichCourse(s.course_id);
      const d = new Date(weekStart);
      d.setDate(d.getDate() + (s.day_of_week ?? 0));
      out.push({ ...s, ...enriched, _type: "timetable", _date: d });
    });
    return out;
  };

  // Personal events as dated
  const personalEventsDated = useMemo(() => {
    return personalEvents.map(e => {
      const dateStr = e.start_time ? `${e.date}T${e.start_time}:00` : `${e.date}T00:00:00`;
      return { ...e, _type: "personal", _date: new Date(dateStr) };
    });
  }, [personalEvents]);

  // ---------- Actions ----------
  const savePersonal = async (payload) => {
    try {
      if (openPersonal?.id) {
        await api.patch(`/personal-events/${openPersonal.id}`, payload);
        toast.success("Rappel modifié");
      } else {
        await api.post("/personal-events", payload);
        toast.success("Rappel ajouté");
      }
      setOpenPersonal(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deletePersonal = async (id) => {
    if (!window.confirm("Supprimer ce rappel ?")) return;
    try { await api.delete(`/personal-events/${id}`); toast.success("Supprimé"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveAssign = async (payload) => {
    try {
      if (openAssign?.id) {
        await api.patch(`/assignments/${openAssign.id}`, payload);
        toast.success("Devoir modifié");
      } else {
        await api.post("/assignments", payload);
        toast.success("Devoir créé");
      }
      setOpenAssign(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteAssign = async (id) => {
    if (!window.confirm("Supprimer ce devoir ?")) return;
    try { await api.delete(`/assignments/${id}`); toast.success("Supprimé"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const publishAssign = async (id) => {
    try { await api.post(`/assignments/${id}/publish`); toast.success("Devoir publié aux élèves"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveSlot = async (payload, force = false) => {
    try {
      const url = openSlot?.id ? `/timetable-slots/${openSlot.id}` : "/timetable-slots";
      const method = openSlot?.id ? api.patch : api.post;
      await method(url + (force ? "?force=true" : ""), payload);
      toast.success(openSlot?.id ? "Créneau modifié" : "Créneau créé");
      setOpenSlot(null);
      loadAll();
    } catch (e) {
      if (e?.response?.status === 409) {
        const detail = e.response.data?.detail || {};
        const cs = (detail.conflicts || []).map(c => `${c.reason} · ${c.start_time}-${c.end_time}`).join(" · ");
        if (window.confirm(`Conflit(s) détecté(s) : ${cs}\n\nForcer la création malgré tout ?`)) {
          saveSlot(payload, true);
        }
      } else {
        toast.error(formatApiError(e));
      }
    }
  };
  const deleteSlot = async (id) => {
    if (!window.confirm("Supprimer ce créneau ?")) return;
    try { await api.delete(`/timetable-slots/${id}`); toast.success("Supprimé"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- Header ----------
  const shiftCursor = (delta) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + delta);
    else if (view === "week") d.setDate(d.getDate() + delta * 7);
    else d.setDate(d.getDate() + delta * 14);
    setCursor(d);
  };
  const cursorLabel = useMemo(() => {
    if (view === "month") return cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    if (view === "week") {
      const ws = startOfWeek(cursor);
      const we = new Date(ws); we.setDate(we.getDate() + 6);
      return `${ws.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} — ${we.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}`;
    }
    return cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [cursor, view]);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Planification</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <CalendarIcon size={30} weight="duotone" className="text-primary" /> Agenda
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isStudent
              ? "Vos cours, devoirs à rendre et évaluations."
              : "Horaires, salles, devoirs et évaluations. Créez et modifiez depuis cette page."}
          </p>
        </div>
        {canEditAssign && !isStudent && (
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setOpenAssign({})} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-assign">
              <Plus size={16} className="mr-1" /> Nouveau devoir
            </Button>
            {canEditSlot && (
              <Button onClick={() => setOpenSlot({})} variant="outline" className="rounded-full" data-testid="btn-new-slot">
                <Plus size={16} className="mr-1" /> Nouveau créneau
              </Button>
            )}
            <Button onClick={() => setOpenPersonal({})} variant="outline" className="rounded-full" data-testid="btn-new-personal">
              <Plus size={16} className="mr-1" /> Nouveau rappel
            </Button>
          </div>
        )}
        {isStudent && (
          <Button onClick={() => setOpenPersonal({})} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-personal">
            <Plus size={16} className="mr-1" /> Nouveau rappel
          </Button>
        )}
      </header>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="month" data-testid="view-month"><CalendarIcon size={14} className="mr-1" /> Mois</TabsTrigger>
            <TabsTrigger value="week" data-testid="view-week"><GridFour size={14} className="mr-1" /> Semaine</TabsTrigger>
            <TabsTrigger value="list" data-testid="view-list"><List size={14} className="mr-1" /> Liste</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shiftCursor(-1)} data-testid="btn-prev"><CaretLeft size={16} /></Button>
          <div className="text-sm font-semibold capitalize min-w-[10rem] text-center" data-testid="cursor-label">{cursorLabel}</div>
          <Button variant="outline" size="icon" onClick={() => shiftCursor(1)} data-testid="btn-next"><CaretRight size={16} /></Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())}>Aujourd'hui</Button>
        </div>
      </div>

      {/* Views */}
      {view === "month" && (
        <MonthView cursor={cursor} assignments={assignmentEvents} slots={slots} personalEvents={personalEventsDated} enrichCourse={enrichCourse}
                   onOpenAssign={(a) => isStudent ? setOpenSubmit(a) : setOpenAssign(a)}
                   onOpenPersonal={(p) => setOpenPersonal(p)}
                   onOpenSlot={canEditSlot ? (s) => setOpenSlot(s) : null} />
      )}
      {view === "week" && (
        <WeekView cursor={cursor} assignments={assignmentEvents} slots={expandSlotsForWeek(startOfWeek(cursor))} personalEvents={personalEventsDated}
                  onOpenAssign={(a) => isStudent ? setOpenSubmit(a) : setOpenAssign(a)}
                  onOpenPersonal={(p) => setOpenPersonal(p)}
                  onOpenSlot={canEditSlot ? (s) => setOpenSlot(s) : null} />
      )}
      {view === "list" && (
        <ListView cursor={cursor} assignments={assignmentEvents} slots={slots} personalEvents={personalEventsDated} enrichCourse={enrichCourse}
                  canEditSlot={canEditSlot} canEditAssign={canEditAssign} isStudent={isStudent}
                  onSubmit={(a) => setOpenSubmit(a)} onEditAssign={setOpenAssign} onEditSlot={setOpenSlot}
                  onEditPersonal={setOpenPersonal} onDeletePersonal={deletePersonal}
                  onDeleteSlot={deleteSlot} onDeleteAssign={deleteAssign} />
      )}

      {/* Dialogs */}
      {openPersonal !== null && (
        <PersonalEventDialog event={openPersonal} onClose={() => setOpenPersonal(null)}
                             onSave={savePersonal} onDelete={deletePersonal} />
      )}
      {openAssign !== null && (
        <AssignmentDialog assign={openAssign} courses={courses} subjects={subjectById}
                          classes={classById} onClose={() => setOpenAssign(null)}
                          onSave={saveAssign} onDelete={deleteAssign} onPublish={publishAssign} />
      )}
      {openSlot !== null && (
        <SlotDialog slot={openSlot} courses={courses} subjectById={subjectById} classById={classById}
                    onClose={() => setOpenSlot(null)} onSave={saveSlot} onDelete={deleteSlot} />
      )}
      {openSubmit !== null && (
        <SubmitDialog assign={openSubmit} onClose={() => { setOpenSubmit(null); loadAll(); }} />
      )}
    </div>
  );
}

/* ---------- Month view (calendar grid) ---------- */
function MonthView({ cursor, assignments, slots, personalEvents, enrichCourse, onOpenAssign, onOpenPersonal, onOpenSlot }) {
  const mStart = startOfMonth(cursor);
  const gridStart = startOfWeek(mStart);
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart); d.setDate(d.getDate() + i); return d;
  });
  const monthIndex = cursor.getMonth();

  const eventsByDay = useMemo(() => {
    const m = {};
    assignments.forEach(a => {
      const k = ymd(a._date);
      (m[k] = m[k] || []).push(a);
    });
    return m;
  }, [assignments]);
  const personalByDay = useMemo(() => {
    const m = {};
    (personalEvents || []).forEach(e => {
      const k = ymd(e._date);
      (m[k] = m[k] || []).push(e);
    });
    return m;
  }, [personalEvents]);

  return (
    <Card className="overflow-hidden">
      <div className="grid grid-cols-7 border-b bg-muted/50">
        {DAYS_SHORT.map(d => <div key={d} className="p-2 text-xs uppercase tracking-wider text-muted-foreground text-center font-semibold">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 grid-rows-6 min-h-[520px]">
        {days.map((d, i) => {
          const inMonth = d.getMonth() === monthIndex;
          const isToday = ymd(d) === ymd(new Date());
          const key = ymd(d);
          const evts = eventsByDay[key] || [];
          const pers = personalByDay[key] || [];
          const daySlots = slots.filter(s => s.day_of_week === ((d.getDay() + 6) % 7));
          return (
            <div key={i} className={`border-b border-r p-1 min-h-[80px] ${!inMonth ? "bg-muted/30 text-muted-foreground" : ""} ${isToday ? "bg-primary/5" : ""}`}
                 data-testid={`day-${key}`}>
              <div className={`text-xs font-mono ${isToday ? "text-primary font-bold" : ""}`}>{d.getDate()}</div>
              <div className="space-y-0.5 mt-1">
                {evts.slice(0, 2).map(a => (
                  <button key={a.id} onClick={() => onOpenAssign(a)}
                          className="w-full text-left text-[10px] px-1 py-0.5 rounded bg-accent/15 text-accent truncate hover:bg-accent/25"
                          data-testid={`month-evt-${a.id}`}>
                    {a.title}
                  </button>
                ))}
                {pers.slice(0, 2).map(p => (
                  <button key={p.id} onClick={() => onOpenPersonal(p)}
                          className="w-full text-left text-[10px] px-1 py-0.5 rounded bg-secondary/15 text-secondary truncate hover:bg-secondary/25"
                          data-testid={`month-personal-${p.id}`}>
                    {p.title}
                  </button>
                ))}
                {evts.length > 2 && <div className="text-[9px] text-muted-foreground pl-1">+{evts.length - 2} devoirs</div>}
                {daySlots.length > 0 && inMonth && (
                  <div className="text-[9px] text-secondary flex items-center gap-1 pl-1"><Clock size={8} /> {daySlots.length} cours</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ---------- Week view (day × hour grid) ---------- */
function WeekView({ cursor, assignments, slots, personalEvents, onOpenAssign, onOpenPersonal, onOpenSlot }) {
  const ws = startOfWeek(cursor);
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(ws); d.setDate(d.getDate() + i); return d; });

  const dueByDay = useMemo(() => {
    const m = {};
    assignments.forEach(a => {
      const k = ymd(a._date);
      (m[k] = m[k] || []).push(a);
    });
    return m;
  }, [assignments]);

  return (
    <Card className="overflow-x-auto">
      <div className="min-w-[900px]">
        {/* Header */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b bg-muted/50">
          <div />
          {weekDays.map((d, i) => (
            <div key={i} className={`p-2 text-center text-xs font-semibold ${ymd(d) === ymd(new Date()) ? "text-primary" : ""}`}>
              {DAYS_SHORT[i]} <span className="text-muted-foreground">{d.getDate()}</span>
            </div>
          ))}
        </div>
        {/* Assignment strip */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b bg-accent/5">
          <div className="p-1 text-[9px] text-accent text-right pr-2 uppercase tracking-wider">Devoirs</div>
          {weekDays.map((d, i) => {
            const evts = dueByDay[ymd(d)] || [];
            return (
              <div key={i} className="p-1 border-l space-y-0.5 min-h-[26px]">
                {evts.map(a => (
                  <button key={a.id} onClick={() => onOpenAssign(a)}
                          className="w-full text-left text-[10px] px-1 py-0.5 rounded bg-accent/20 text-accent truncate hover:bg-accent/30"
                          data-testid={`week-due-${a.id}`}>
                    {a.title}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
        {/* Grid */}
        {HOURS.map(h => (
          <div key={h} className="grid grid-cols-[60px_repeat(7,1fr)] border-b">
            <div className="p-1 text-[10px] text-muted-foreground text-right pr-2 tabular-nums">{String(h).padStart(2, "0")}:00</div>
            {weekDays.map((d, i) => {
              const dow = (d.getDay() + 6) % 7;
              const cellSlots = slots.filter(s => {
                if (s.day_of_week !== dow) return false;
                const [sh] = (s.start_time || "00:00").split(":").map(Number);
                return sh === h;
              });
              return (
                <div key={i} className="p-1 border-l min-h-[48px] relative">
                  {cellSlots.map(s => (
                    <button key={s.id} onClick={() => onOpenSlot && onOpenSlot(s)}
                            className="block w-full text-left text-[10px] px-1 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 mb-0.5"
                            data-testid={`week-slot-${s.id}`}>
                      <div className="font-semibold truncate">{s.subject_name}</div>
                      <div className="text-[9px] opacity-70">{s.start_time}-{s.end_time}{s.room ? ` · ${s.room}` : ""}</div>
                      <div className="text-[9px] opacity-70 truncate">{s.class_name}</div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ---------- List view ---------- */
function ListView({ cursor, assignments, slots, personalEvents, enrichCourse, canEditSlot, canEditAssign, isStudent,
                   onSubmit, onEditAssign, onEditSlot, onEditPersonal, onDeletePersonal, onDeleteSlot, onDeleteAssign }) {
  const from = new Date(cursor); from.setDate(from.getDate() - 7);
  const to = new Date(cursor); to.setDate(to.getDate() + 21);
  const inRange = assignments
    .filter(a => a._date >= from && a._date <= to)
    .sort((x, y) => x._date - y._date);
  const persInRange = (personalEvents || [])
    .filter(p => p._date >= from && p._date <= to)
    .sort((x, y) => x._date - y._date);
  const weeklySlots = slots.map(s => ({ ...s, ...enrichCourse(s.course_id) }))
    .sort((a, b) => (a.day_of_week - b.day_of_week) || a.start_time.localeCompare(b.start_time));

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-6 rounded-full bg-accent" />
          <h3 className="font-heading font-semibold">Devoirs & Évaluations <span className="text-muted-foreground text-sm font-normal">({inRange.length})</span></h3>
        </div>
        {inRange.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Aucun devoir dans la période.</div>
        ) : (
          <div className="space-y-2">
            {inRange.map(a => {
              const overdue = a._date < new Date() && !a.submission;
              return (
                <div key={a.id} className={`border rounded-lg p-3 flex items-center justify-between gap-3 hover:bg-muted/40 ${overdue ? "border-accent/40" : ""}`}
                     data-testid={`list-assign-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                      <span>{a.subject_name}</span> · <span>{KIND_LABEL[a.kind] || "Devoir"}</span>
                      {a.class_name && a.class_name !== "—" && <><span>·</span><span>{a.class_name}</span></>}
                    </div>
                    <div className="font-medium truncate">{a.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1">
                      <span className={`flex items-center gap-1 ${overdue ? "text-accent" : ""}`}>
                        <Clock size={12} /> {a._date.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {a.max_score && <span>Sur {a.max_score}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {a.submission?.status === "graded" && <Badge className="bg-secondary text-secondary-foreground">{a.submission.grade}/{a.max_score}</Badge>}
                    {a.submission?.status === "submitted" && <Badge variant="outline">Rendu</Badge>}
                    {isStudent && !a.submission?.grade && (
                      <Button size="sm" variant="outline" onClick={() => onSubmit(a)} data-testid={`btn-submit-${a.id}`}>
                        <PaperPlaneTilt size={14} className="mr-1" /> {a.submission ? "Modifier" : "Remettre"}
                      </Button>
                    )}
                    {canEditAssign && !isStudent && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => onEditAssign(a)} data-testid={`btn-edit-assign-${a.id}`}><PencilSimple size={16} className="text-primary" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => onDeleteAssign(a.id)}><Trash size={16} className="text-destructive" /></Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-6 rounded-full bg-secondary" />
          <h3 className="font-heading font-semibold">Mes rappels personnels <span className="text-muted-foreground text-sm font-normal">({persInRange.length})</span></h3>
        </div>
        {persInRange.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Aucun rappel. Cliquez "Nouveau rappel" pour ajouter un moment d'étude ou un pense-bête.</div>
        ) : (
          <div className="space-y-2">
            {persInRange.map(p => (
              <div key={p.id} className="border rounded-lg p-3 flex items-center justify-between gap-3 hover:bg-muted/40" data-testid={`list-personal-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-secondary">{p.kind === "study" ? "Étude" : p.kind === "reminder" ? "Rappel" : "Autre"}</div>
                  <div className="font-medium truncate">{p.title}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1"><Clock size={12} /> {p._date.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: p.start_time ? "2-digit" : undefined, minute: p.start_time ? "2-digit" : undefined })}
                    {p.end_time ? ` — ${p.end_time}` : ""}</span>
                    {p.location && <span className="flex items-center gap-1"><MapPin size={12} /> {p.location}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => onEditPersonal(p)}><PencilSimple size={16} className="text-primary" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => onDeletePersonal(p.id)}><Trash size={16} className="text-destructive" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-6 rounded-full bg-primary" />
          <h3 className="font-heading font-semibold">Emploi du temps hebdomadaire <span className="text-muted-foreground text-sm font-normal">({weeklySlots.length} créneaux)</span></h3>
        </div>
        {weeklySlots.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Aucun créneau. {canEditSlot && "Créez-en un depuis \"Nouveau créneau\"."}</div>
        ) : (
          <div className="space-y-2">
            {weeklySlots.map(s => (
              <div key={s.id} className="border rounded-lg p-3 flex items-center justify-between gap-3 hover:bg-muted/40" data-testid={`list-slot-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {DAYS_FR[s.day_of_week]} · {s.subject_name}
                  </div>
                  <div className="font-medium">{s.class_name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1"><Clock size={12} /> {s.start_time}—{s.end_time}</span>
                    {s.room && <span className="flex items-center gap-1"><MapPin size={12} /> {s.room}</span>}
                    {s.teacher_names?.length > 0 && <span>{s.teacher_names.join(", ")}</span>}
                  </div>
                </div>
                {canEditSlot && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => onEditSlot(s)} data-testid={`btn-edit-slot-${s.id}`}><PencilSimple size={16} className="text-primary" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => onDeleteSlot(s.id)}><Trash size={16} className="text-destructive" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------- Assignment Dialog (teacher/admin) ---------- */
function AssignmentDialog({ assign, courses, subjects, classes, onClose, onSave, onDelete, onPublish }) {
  const editing = !!assign?.id;
  const [form, setForm] = useState({
    course_id: assign?.course_id || "",
    title: assign?.title || "",
    description: assign?.description || "",
    kind: assign?.kind || "homework",
    due_date: assign?.due_date ? assign.due_date.slice(0, 16) : "",
    max_score: assign?.max_score || 20,
    weight: assign?.weight || 1,
    period: assign?.period || "",
    status: assign?.status || "draft",
    attachments: assign?.attachments || [],
    allow_late_submission: assign?.allow_late_submission ?? true,
  });
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const r = await api.post("/uploads/document", { data_url: b64, filename: file.name, kind: "assignment" });
      setForm(f => ({ ...f, attachments: [...f.attachments, { url: r.data.url, filename: r.data.filename, size: r.data.size, mime: r.data.mime }] }));
      toast.success(`Fichier joint : ${file.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };
  const onFiles = async (e) => {
    for (const f of e.target.files || []) await uploadFile(f);
    e.target.value = "";
  };

  const submit = () => {
    if (!form.course_id) return toast.error("Choisissez un cours");
    if (!form.title) return toast.error("Titre requis");
    const payload = { ...form, due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
                      max_score: Number(form.max_score), weight: Number(form.weight) };
    onSave(payload);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? `Modifier · ${assign.title}` : "Nouveau devoir"}</DialogTitle>
          <DialogDescription>Un devoir associé à un cours. Les élèves reçoivent une notification à la publication.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label>Cours (matière × classe)</Label>
            <Select value={form.course_id} onValueChange={v => setForm({ ...form, course_id: v })}>
              <SelectTrigger data-testid="assign-course"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>
                {courses.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {subjects[c.subject_id]?.name || "?"} — {classes[c.class_group_id]?.name || "?"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Titre</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="assign-title" /></div>
          <div><Label>Description / Consignes</Label>
            <Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="assign-desc" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label>Type</Label>
              <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Échéance</Label><Input type="datetime-local" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="assign-due" /></div>
            <div><Label>Sur (max)</Label><Input type="number" value={form.max_score} onChange={e => setForm({ ...form, max_score: e.target.value })} /></div>
            <div><Label>Coef.</Label><Input type="number" step="0.5" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} /></div>
          </div>
          <div>
            <Label>Pièces jointes (PDF, Word, Excel, images, ZIP — max 20 Mo/fichier)</Label>
            <div className="space-y-2">
              {form.attachments.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-muted rounded text-xs">
                  <span className="flex items-center gap-2 min-w-0"><Paperclip size={12} /><span className="truncate">{a.filename}</span></span>
                  <div className="flex items-center gap-1 shrink-0">
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-primary"><DownloadSimple size={14} /></a>
                    <button onClick={() => setForm(f => ({ ...f, attachments: f.attachments.filter((_, j) => j !== i) }))}><X size={14} className="text-destructive" /></button>
                  </div>
                </div>
              ))}
              <Input type="file" multiple accept={ACCEPT_MIME} onChange={onFiles} disabled={uploading} data-testid="assign-files" />
              {uploading && <div className="text-xs text-muted-foreground">Téléversement…</div>}
            </div>
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {editing && <Button variant="outline" onClick={() => onDelete(assign.id)} className="text-destructive"><Trash size={14} className="mr-1" /> Supprimer</Button>}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          {editing && assign.status !== "published" && <Button variant="outline" onClick={() => { submit(); onPublish(assign.id); }}>Enregistrer & Publier</Button>}
          <Button onClick={submit} data-testid="assign-save">{editing ? "Enregistrer" : "Créer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Slot Dialog (teacher/admin) ---------- */
function SlotDialog({ slot, courses, subjectById, classById, onClose, onSave, onDelete }) {
  const editing = !!slot?.id;
  const [form, setForm] = useState({
    course_id: slot?.course_id || "",
    day_of_week: slot?.day_of_week ?? 0,
    start_time: slot?.start_time || "08:00",
    end_time: slot?.end_time || "09:00",
    room: slot?.room || "",
    week_pattern: slot?.week_pattern || "weekly",
  });
  const submit = () => {
    if (!form.course_id) return toast.error("Choisissez un cours");
    if (form.start_time >= form.end_time) return toast.error("Heure de fin invalide");
    onSave({ ...form, day_of_week: Number(form.day_of_week) });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier le créneau" : "Nouveau créneau (emploi du temps)"}</DialogTitle>
          <DialogDescription>Un créneau récurrent (hebdomadaire) lié à un cours.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label>Cours</Label>
            <Select value={form.course_id} onValueChange={v => setForm({ ...form, course_id: v })}>
              <SelectTrigger data-testid="slot-course"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>
                {courses.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {subjectById[c.subject_id]?.name || "?"} — {classById[c.class_group_id]?.name || "?"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Jour</Label>
              <Select value={String(form.day_of_week)} onValueChange={v => setForm({ ...form, day_of_week: v })}>
                <SelectTrigger data-testid="slot-day"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS_FR.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Semaine</Label>
              <Select value={form.week_pattern} onValueChange={v => setForm({ ...form, week_pattern: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Toutes les semaines</SelectItem>
                  <SelectItem value="A">Semaine A</SelectItem>
                  <SelectItem value="B">Semaine B</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Début</Label><Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} data-testid="slot-start" /></div>
            <div><Label>Fin</Label><Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} data-testid="slot-end" /></div>
            <div><Label>Salle</Label><Input value={form.room} onChange={e => setForm({ ...form, room: e.target.value })} placeholder="Ex: B12" data-testid="slot-room" /></div>
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {editing && <Button variant="outline" onClick={() => onDelete(slot.id)} className="text-destructive"><Trash size={14} className="mr-1" /> Supprimer</Button>}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} data-testid="slot-save">{editing ? "Enregistrer" : "Créer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Personal Event Dialog (all roles, own agenda) ---------- */
function PersonalEventDialog({ event, onClose, onSave, onDelete }) {
  const editing = !!event?.id;
  const [form, setForm] = useState({
    title: event?.title || "",
    description: event?.description || "",
    date: event?.date || ymd(new Date()),
    start_time: event?.start_time || "",
    end_time: event?.end_time || "",
    kind: event?.kind || "reminder",
    location: event?.location || "",
  });
  const submit = () => {
    if (!form.title) return toast.error("Titre requis");
    if (!form.date) return toast.error("Date requise");
    onSave(form);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier le rappel" : "Nouveau rappel personnel"}</DialogTitle>
          <DialogDescription>Visible uniquement dans votre propre agenda (moments d'étude, pense-bêtes…).</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div><Label>Titre</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="personal-title" /></div>
          <div>
            <Label>Type</Label>
            <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="personal-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="reminder">Rappel / Pense-bête</SelectItem>
                <SelectItem value="study">Moment d'étude</SelectItem>
                <SelectItem value="other">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="personal-date" /></div>
            <div><Label>Début</Label><Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} /></div>
            <div><Label>Fin</Label><Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} /></div>
          </div>
          <div><Label>Lieu (optionnel)</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Ex: Bibliothèque" /></div>
          <div><Label>Notes</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {editing && <Button variant="outline" onClick={() => onDelete(event.id)} className="text-destructive"><Trash size={14} className="mr-1" /> Supprimer</Button>}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} data-testid="personal-save">{editing ? "Enregistrer" : "Créer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Submit Dialog (student) ---------- */
function SubmitDialog({ assign, onClose }) {
  const [content, setContent] = useState(assign?.submission?.content || "");
  const [attachments, setAttachments] = useState(assign?.submission?.attachments || []);
  const [handwritten, setHandwritten] = useState(!!assign?.submission?.submitted_handwritten);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const r = await api.post("/uploads/document", { data_url: b64, filename: file.name, kind: "submission" });
      setAttachments(a => [...a, { url: r.data.url, filename: r.data.filename, size: r.data.size, mime: r.data.mime }]);
      toast.success(`Fichier joint : ${file.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };
  const onFiles = async (e) => {
    for (const f of e.target.files || []) await uploadFile(f);
    e.target.value = "";
  };
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/submissions", { assignment_id: assign.id, content, attachments, submitted_handwritten: handwritten });
      toast.success("Remise envoyée");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Remettre : {assign.title}</DialogTitle>
          <DialogDescription>
            {assign.subject_name} · {KIND_LABEL[assign.kind] || "Devoir"}
            {assign.due_date ? ` · à rendre avant ${new Date(assign.due_date).toLocaleString("fr-FR")}` : ""}
          </DialogDescription>
        </DialogHeader>
        {assign.description && <div className="text-sm bg-muted/50 rounded p-3 whitespace-pre-wrap max-h-32 overflow-y-auto">{assign.description}</div>}
        {assign.attachments?.length > 0 && (
          <div>
            <Label>Consignes / Ressources</Label>
            <div className="space-y-1 mt-1">
              {assign.attachments.map((a, i) => (
                <a key={i} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs p-2 bg-muted rounded hover:bg-muted/70">
                  <DownloadSimple size={12} /> {a.filename}
                </a>
              ))}
            </div>
          </div>
        )}
        <div><Label>Ma réponse (facultatif)</Label>
          <Textarea rows={4} value={content} onChange={e => setContent(e.target.value)} data-testid="submit-content" />
        </div>
        <div>
          <Label>Mes fichiers (PDF, Word, Excel, images, ZIP — 20 Mo · jusqu'à 2 fichiers)</Label>
          <div className="space-y-2">
            {attachments.map((a, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-muted rounded text-xs">
                <span className="flex items-center gap-2 min-w-0"><Paperclip size={12} /><span className="truncate">{a.filename}</span></span>
                <button onClick={() => setAttachments(l => l.filter((_, j) => j !== i))}><X size={14} className="text-destructive" /></button>
              </div>
            ))}
            <Input type="file" multiple accept={ACCEPT_MIME} onChange={onFiles} disabled={uploading} data-testid="submit-files" />
            {uploading && <div className="text-xs text-muted-foreground">Téléversement…</div>}
          </div>
        </div>
        <label className="flex items-start gap-2 p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer" data-testid="handwritten-label">
          <input type="checkbox" checked={handwritten} onChange={e => setHandwritten(e.target.checked)}
                 className="h-4 w-4 rounded border-input mt-0.5" data-testid="checkbox-handwritten" />
          <span className="text-sm">
            <b>Devoir remis en manuscrit</b>
            <span className="block text-[11px] text-muted-foreground">Cochez si vous avez rendu ce travail sur papier au professeur.</span>
          </span>
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving || uploading} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-do-submit">
            <PaperPlaneTilt size={14} className="mr-1" /> Remettre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
